import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL;

const categoryNames = {
  gitsawe: 'ግጻዌ', sinksar: 'ስንክሳር', wudasie: 'ውዳሴ ማርያም',
  mahlet: 'ማኅሌት', seatat: 'ሰዓታት', filseta: 'ፍልሰታ',
  hibuat: 'ኅቡዓት', bible: 'መጽሐፍ ቅዱስ', news: 'ዜናዎች',
  posting: 'ልጥፎች', books: 'መጻሕፍት'
};

const Bookmarks = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bookmarks, setBookmarks] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [selectedType, setSelectedType] = useState('ሁሉም');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  const loadBookmarks = useCallback(async () => {
    setLoading(true);
    if (user?.uid) {
      // Load from server
      try {
        const res = await fetch(`${API}/interactions?userId=${user.uid}`);
        if (res.ok) {
          const data = await res.json();
          const bms = data.filter(i => i.action === 'bookmark');
          setBookmarks(bms);
          setFiltered(bms);
        }
      } catch { setBookmarks([]); }
    } else {
      // Fallback: localStorage
      const data = JSON.parse(localStorage.getItem('gitsawe_bookmark_data') || '{}');
      const arr = Object.values(data).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setBookmarks(arr);
      setFiltered(arr);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { loadBookmarks(); }, [loadBookmarks]);

  useEffect(() => {
    let result = bookmarks;
    if (selectedType !== 'ሁሉም') result = result.filter(b => b.contentType === selectedType || b.type === selectedType);
    if (searchTerm) result = result.filter(b => (b.title || '').toLowerCase().includes(searchTerm.toLowerCase()));
    setFiltered(result);
  }, [bookmarks, selectedType, searchTerm]);

  const removeBookmark = async (bookmark) => {
    const id = bookmark._id || bookmark.id;
    if (user?.uid) {
      await fetch(`${API}/interactions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.uid, contentId: bookmark.contentId, contentType: bookmark.contentType, action: 'bookmark' })
      });
    } else {
      const data = JSON.parse(localStorage.getItem('gitsawe_bookmark_data') || '{}');
      const key = `${bookmark.type}_${bookmark.id}`;
      delete data[key];
      localStorage.setItem('gitsawe_bookmark_data', JSON.stringify(data));
    }
    setBookmarks(p => p.filter(b => (b._id || b.id) !== id));
  };

  const types = ['ሁሉም', ...new Set(bookmarks.map(b => b.contentType || b.type).filter(Boolean))];

  const goToContent = (b) => {
    const type = b.contentType || b.type;
    const routes = { gitsawe: '/gitsawie', sinksar: '/sinksar', wudasie: '/wudasie', mahlet: '/mahlet', seatat: '/seatat', filseta: '/filseta', hibuat: '/hibuat', bible: '/bible', news: '/news', posting: '/posting', books: '/books' };
    if (routes[type]) navigate(routes[type]);
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-main-title">🔖 የኔ ምርጦች</h1>
      </div>

      {!user && (
        <div className="content-container text-center">
          <p className="text-sm text-gray-500 mb-3">ምርጦችዎን ለማየት ይግቡ</p>
          <button onClick={() => navigate('/login')} className="btn-primary">👤 ግባ</button>
        </div>
      )}

      {loading ? (
        <div className="loading-container"><div className="loading-spinner"></div></div>
      ) : bookmarks.length === 0 ? (
        <div className="no-results">
          <div className="no-results-icon">📑</div>
          <p className="no-results-title">ምንም ምርጦች አልተቀመጡም</p>
          <p className="no-results-subtitle">በማንኛውም ገጽ 🔖 ቁልፉን ጫን ለማስቀመጥ</p>
          <button onClick={() => navigate('/')} className="btn-primary mt-4">ወደ መነሻ</button>
        </div>
      ) : (
        <>
          <div className="controls-container">
            <div className="controls-grid">
              <div className="control-group">
                <label className="control-label">🔍 ፈልግ</label>
                <input type="text" placeholder="ምርጦችን ይፈልጉ..." value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)} className="control-input" />
              </div>
              <div className="control-group">
                <label className="control-label">🏷️ ምድብ</label>
                <select value={selectedType} onChange={e => setSelectedType(e.target.value)} className="control-select">
                  {types.map(t => <option key={t} value={t}>{categoryNames[t] || t}</option>)}
                </select>
              </div>
              <div className="control-group">
                <label className="control-label">ጠቅላላ</label>
                <p className="text-sm font-semibold pt-2">{filtered.length} ምርጦች</p>
              </div>
            </div>
          </div>

          <div className="card-grid">
            {filtered.map((b, i) => (
              <div key={b._id || b.id || i} className="card-item">
                <div className="card-header">
                  <div className="card-title">
                    <span className="card-number">{categoryNames[b.contentType || b.type] || b.contentType || b.type}</span>
                    {b.title || 'ርዕስ የለም'}
                  </div>
                  <button onClick={() => removeBookmark(b)} className="text-red-400 hover:text-red-600 text-sm" title="አስወግድ">🗑️</button>
                </div>
                {b.content && <p className="text-xs text-gray-500 mb-2 line-clamp-2">{b.content}</p>}
                {b.createdAt && <p className="text-xs text-gray-400 mb-2">{new Date(b.createdAt).toLocaleDateString('am-ET')}</p>}
                <button onClick={() => goToContent(b)} className="btn-primary text-xs">ሙሉውን አንብብ</button>
              </div>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="no-results">
              <p className="no-results-title">ምንም ውጤት አልተገኘም</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Bookmarks;
